import {
  useDepositWithdrawRequestMutation,
  useCryptoDepositRequestMutation,
} from '@/store/api/accountApi';
import { useTranslation } from '@/i18n';
import { getApiErrorMessage, showToast } from '@/utils';
import { formatInr } from '@/components/deposit/depositOptions';
import type { WithdrawAccount } from './withdrawAccounts';
import { WithdrawAccountIcon } from './WithdrawAccountList';

type Props = {
  account: WithdrawAccount;
  amount: number;
  onSubmitted: () => void;
};

const Row = ({ label, value }: { label: string; value?: string | null }) =>
  value ? (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="shrink-0 text-shell-muted">{label}</span>
      <span className="break-all text-right font-medium text-shell-fg">{value}</span>
    </div>
  ) : null;

/**
 * Step 3: review the account and amount, then submit with the same requests as the
 * /withdraw page.
 */
const WithdrawConfirmStep = ({ account, amount, onSubmitted }: Props) => {
  const { t } = useTranslation();
  const [submitWithdraw, { isLoading: isSubmittingFiat }] = useDepositWithdrawRequestMutation();
  const [submitCrypto, { isLoading: isSubmittingCrypto }] = useCryptoDepositRequestMutation();
  const isBusy = isSubmittingFiat || isSubmittingCrypto;

  // Crypto withdrawals are requested in USDT, converted from coins at the wallet's rate,
  // exactly as the /withdraw page does.
  const usdtAmount = account.kind === 'crypto' && account.rate ? amount / account.rate : undefined;
  const canSubmit = !isBusy && (account.kind !== 'crypto' || Boolean(usdtAmount));
  const titles = {
    bank: t('withdraw.bankAccount'),
    upi: 'UPI',
    crypto: t('withdraw.cryptoWallet'),
  };
  const title = titles[account.kind];

  const handleSubmit = async () => {
    if (!canSubmit) return;
    try {
      const response =
        account.kind === 'crypto'
          ? await submitCrypto({
              type: 'Debit',
              amount: usdtAmount!,
              cryptoId: account.account.id,
            }).unwrap()
          : await submitWithdraw({
              type: 'Debit',
              amount,
              method: account.kind,
              ...(account.kind === 'bank'
                ? { bankId: Number(account.account.id) }
                : { UPI: Number(account.account.id) }),
            }).unwrap();
      showToast(response.message || 'Withdrawal request submitted successfully');
      onSubmitted();
    } catch (error: unknown) {
      showToast(getApiErrorMessage(error, 'Failed to submit withdrawal request'), 'error');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 border-b border-shell-border pb-3">
        <WithdrawAccountIcon kind={account.kind} />
        <span className="text-base font-medium">{title}</span>
      </div>

      <div className="space-y-2 rounded-xl bg-shell-raised px-4 py-3">
        {account.kind === 'bank' && (
          <>
            <Row label={t('withdraw.accountHolder')} value={account.account.accountHolder} />
            <Row label={t('withdraw.accountNumber')} value={account.account.accountNumber} />
            <Row label={t('withdraw.ifsc')} value={account.account.ifsc} />
          </>
        )}
        {account.kind === 'upi' && (
          <>
            <Row label={t('deposit.name')} value={account.account.name} />
            <Row label={t('deposit.upiId')} value={account.account.upiId} />
          </>
        )}
        {account.kind === 'crypto' && (
          <Row label={t('deposit.walletAddress')} value={account.account.walletAddress} />
        )}
        <Row label={t('withdraw.amount')} value={formatInr(amount)} />
        {usdtAmount && (
          <Row
            label={t('withdraw.youReceive')}
            value={`${usdtAmount.toLocaleString('en-IN', { maximumFractionDigits: 6 })} USDT`}
          />
        )}
      </div>

      {account.kind === 'crypto' && !usdtAmount && (
        <p className="rounded-lg border border-danger-red px-3 py-2 text-sm text-danger-red">
          {t('deposit.rateUnavailable')}
        </p>
      )}

      <button
        type="button"
        onClick={handleSubmit}
        disabled={!canSubmit}
        className="h-12 w-full rounded-xl bg-gold text-base font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isBusy ? t('withdraw.processing') : t('withdraw.submit', { amount: formatInr(amount) })}
      </button>
    </div>
  );
};

export default WithdrawConfirmStep;
